import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  deleteLabelAutoValidationSchema,
  TDeleteLabelAutoValidation,
  useDeleteLabelAutoMutation,
} from '../../../api/delete-label-auto.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_AUTO_QUERY_KEY } from '../../../api/get-list-label-auto.api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Spinner } from '@/components/_ui/spinner';

export type TDeleteLabelAutoModalType = 'DeleteLabelAutoModal';
export interface IDeleteLabelAutoModalProps {
  autoLabelId: string;
}

const DeleteLabelAutoModal = memo(({ autoLabelId }: IDeleteLabelAutoModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteLabelAutoModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('DeleteLabelAutoModal');
  }, [caseModuleModalRootActions]);

  const form = useForm<TDeleteLabelAutoValidation>({
    resolver: zodResolver(deleteLabelAutoValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelId: '',
    },
  });
  useEffect(() => {
    if (autoLabelId) {
      form.reset({
        labelId: autoLabelId || '',
      });
    }
  }, [autoLabelId, form]);

  const mutationDeleteAutoLabel = useDeleteLabelAutoMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error delete auto label, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_AUTO_QUERY_KEY] })]);
        caseModuleModalRootActions.closeModal('DeleteLabelAutoModal');
        showToast({
          message: 'Auto label deleted successfully!',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitDeleteLabelAuto = useCallback(
    (data: TDeleteLabelAutoValidation) => {
      mutationDeleteAutoLabel.mutate({
        labelId: data?.labelId || '',
      });
    },
    [mutationDeleteAutoLabel]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('DeleteLabelAutoModal');
  }, [caseModuleModalRootActions]);

  return (
    <Modal isOpen={isOpenDeleteLabelAutoModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Delete Auto Label</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitDeleteLabelAuto)(e);
          }}
          id="delete-label-auto-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this auto label?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-label-auto-form" type="submit" disabled={mutationDeleteAutoLabel.isPending}>
          {mutationDeleteAutoLabel.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

DeleteLabelAutoModal.displayName = 'DeleteLabelAutoModal';
export default DeleteLabelAutoModal;
